import {Component, OnDestroy, OnInit} from '@angular/core';
import {FinancialPositionService} from "../../services/financial-position.service";
import {AuthService} from "../../../auth/services/auth.service";
import {Timestamp} from "firebase/firestore";
import {ActivatedRoute, Router} from "@angular/router";
import {FinancialPositionTemplateService} from "../../services/financial-position-template.service";
import {FinancialPosition} from "../../models/financial-position";
import {HeaderService} from "../../../tandem-core/services/header.service";
import {TandemUser} from "../../../auth/models/tandem-user";
import {Validators} from "@angular/forms";
import {LocalStorageService} from "../../../tandem-core/services/local-storage.service";
import {DialogService} from "../../../tandem-core/services/dialog.service";
import {Subject, takeUntil} from "rxjs";

@Component({
  selector: 'tandem-create-financial-position',
  templateUrl: './create-financial-position.component.html',
  styleUrls: ['./create-financial-position.component.scss']
})
export class CreateFinancialPositionComponent implements OnInit, OnDestroy {

  userId: string | undefined;
  financialPosition: FinancialPosition | null = null;
  mostRecent: boolean = false;
  shouldPrompt = true;

  user: TandemUser | null = null;

  private destroy$ = new Subject<void>();
  hasPrompted = false;

  constructor(private auth: AuthService,
              private dialogService: DialogService,
              private financialPositionService: FinancialPositionService,
              private financialPositionTemplateService: FinancialPositionTemplateService,
              private headerService: HeaderService,
              private localStorageService: LocalStorageService,
              private router: Router) {
    this.mostRecent = router.url.includes('most-recent');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.auth.$user.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.user = user;
      this.userId = user?.id;
      if (user) {
        if (this.mostRecent) {
          this.shouldPrompt = false;
          this.localStorageService.removeItem('createFinancialPosition');
          this.financialPositionService.getLatest(user.uid, 'positionDate').pipe(takeUntil(this.destroy$)).subscribe(latestFP => {
            if (this.financialPosition === null) {
              if (latestFP) {
                this.financialPosition = {
                  dateCreated: Timestamp.now(),
                  dateModified: Timestamp.now(),
                  title: `${Timestamp.now().toDate().toDateString()}`,
                  positionDate: Timestamp.now(),
                  userId: this.userId!,
                  netWorth: latestFP.netWorth,
                  assets: latestFP.assets.map(a => {
                    return {
                      ...a,
                      assets: a.assets.map(asset => {
                        return {
                          name: asset.name,
                          value: asset.value,
                          annualPercentageReturn: asset.annualPercentageReturn,
                          liquidityQuantity: asset.liquidityQuantity,
                          liquidityUnits: asset.liquidityUnits,
                          associatedExpenses: asset.associatedExpenses
                        }
                      })
                    }
                  }),
                  liabilities: latestFP.liabilities.map(a => {
                    return {
                      ...a,
                      liabilities: a.liabilities.map(liability => {
                        return {
                          name: liability.name,
                          value: liability.value,
                          interestRate: liability.interestRate,
                          minimumPayment: liability.minimumPayment,
                          termQuantity: liability.termQuantity,
                          termUnits: liability.termUnits,
                        }
                      })
                    }
                  }),
                  assetDetails: latestFP.assetDetails,
                  liabilityDetails: latestFP.liabilityDetails,
                  averageReturn: latestFP.averageReturn,
                  averageInterestRate: latestFP.averageInterestRate,
                  liquidAssets: latestFP.liquidAssets,
                  shortTermLiabilities: latestFP.shortTermLiabilities,
                  totalAssets: latestFP.totalAssets,
                  totalLiabilities: latestFP.totalLiabilities,
                  totalMinimumPayment: latestFP.totalMinimumPayment,
                  visibleToCoach: latestFP.visibleToCoach,
                  assetDescription: latestFP.assetDescription,
                  liabilityDescription: latestFP.liabilityDescription
                }
              } else {
                this.useTemplateForUser(user); // Latest was not found
              }
            }
          });
        } else {
          if (!this.localStorageService.getItem('createFinancialPosition')) {
            this.useTemplateForUser(user);
          } else {
            if (this.shouldPrompt && !this.hasPrompted) {
              this.dialogService.openModal2({
                title: 'Pick Up Where You Left Off',
                content: `Looks like you were working on a new ${this.getTrackingToolName('single').toLowerCase()} before leaving the page. Would you like to pick up where you left off?`,
                type: "confirm",
                actions: [
                  {
                    text: 'Yes Please',
                    role: 'cancel',
                    callback: () => {
                      this.financialPosition = this.localStorageService.getItem('createFinancialPosition');
                      if (this.financialPosition) {
                        this.financialPosition.positionDate = new Timestamp(this.financialPosition.positionDate.seconds, this.financialPosition.positionDate.nanoseconds);
                        this.financialPosition.dateCreated = new Timestamp(this.financialPosition.dateCreated.seconds, this.financialPosition.dateCreated.nanoseconds);
                        this.financialPosition.dateModified = new Timestamp(this.financialPosition.dateModified.seconds, this.financialPosition.dateModified.nanoseconds);
                      }
                    }
                  },
                  {
                    text: 'No, Start Fresh',
                    role: 'confirm',
                    callback: () => {
                      this.useTemplateForUser(user);
                    }
                  },
                ]
              })
              // this.dialogService.openConfirmDialog('Pick Up Where You Left Off',
              //   `Looks like you were working on a new ${this.getTrackingToolName('single').toLowerCase()} before leaving the page. Would you like to pick up where you left off?`,
              //   'Yes Please', 'No, Start Fresh').afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
              //   if (confirmed) {
              //     this.financialPosition = this.localStorageService.getItem('createFinancialPosition');
              //     if (this.financialPosition) {
              //       this.financialPosition.positionDate = new Timestamp(this.financialPosition.positionDate.seconds, this.financialPosition.positionDate.nanoseconds);
              //       this.financialPosition.dateCreated = new Timestamp(this.financialPosition.dateCreated.seconds, this.financialPosition.dateCreated.nanoseconds);
              //       this.financialPosition.dateModified = new Timestamp(this.financialPosition.dateModified.seconds, this.financialPosition.dateModified.nanoseconds);
              //     }
              //   } else {
              //     this.useTemplateForUser(user);
              //   }
              // });
              this.hasPrompted = true;
            }
          }
        }
        this.headerService.setTitle(`Create ${this.getTrackingToolName('single')}`);
        this.headerService.setSubtitle(this.getTrackingToolDescription());
        this.headerService.setBreadcrumbs([
          {
            label: `${this.getTrackingToolName('plural')}`,
            route: 'financial-positions'
          },
          {
            label: `Create${this.mostRecent ? ' From Most Recent' : ''}`,
          }
        ]);
      }
    });
  }

  onCreate(fp: FinancialPosition) {
    fp.assetDescription = fp.assetDescription || '';
    fp.liabilityDescription = fp.liabilityDescription || '';
    this.financialPositionService.add(fp).then(res => {
      this.localStorageService.removeItem('createFinancialPosition');
      this.router.navigate(['financial-positions']);
    })
  }

  private useTemplateForUser(user: TandemUser) {
    this.shouldPrompt = false;
    this.financialPositionTemplateService.getTemplateForUser(user).pipe(takeUntil(this.destroy$)).subscribe(template => {
      if (this.financialPosition === null) {
        this.financialPosition = {
          dateCreated: Timestamp.now(),
          dateModified: Timestamp.now(),
          title: `${Timestamp.now().toDate().toDateString()}`,
          positionDate: Timestamp.now(),
          userId: this.userId!,
          netWorth: 0,
          assets: template.assets.map(ta => {
            return {name: ta.name, assets: [], hoverHint: ta.hoverHint}
          }),
          liabilities: template.liabilities.map(tl => {
            return {name: tl.name, liabilities: [], hoverHint: tl.hoverHint}
          }),
          assetDetails: template.assetDetails,
          liabilityDetails: template.liabilityDetails,
          averageReturn: 0,
          averageInterestRate: 0,
          liquidAssets: 0,
          shortTermLiabilities: 0,
          totalAssets: 0,
          totalLiabilities: 0,
          totalMinimumPayment: 0,
          visibleToCoach: true,
          assetDescription: template.assetDescription,
          liabilityDescription: template.liabilityDescription,
        }
      }
    })
  }

  getTrackingToolName(variant: 'single' | 'plural') {
    if (variant === 'single') {
      return this.user?.customTTMetadata?.fpName ? this.user.customTTMetadata.fpName : 'Financial Position';
    } else {
      return this.user?.customTTMetadata?.fpNamePlural ? `${this.user.customTTMetadata.fpNamePlural}` : 'Financial Positions';
    }
  }

  getTrackingToolDescription() {
    return !!this.user?.customTTMetadata?.fpDescription ? this.user.customTTMetadata.fpDescription : 'A summary of what you own, your assets, and what you owe, your liabilities at a given point in time'
  }
}
